import { Link } from 'react-router-dom';
import ReadinessScore from '../components/ReadinessScore';
import LineChart from '../components/Charts/LineChart';
import RadarChart from '../components/Charts/RadarChart';
import clsx from 'clsx';

export default function ReadinessDashboard() {
  const readinessScore = 74;

  const dimensionScores = {
    labels: ['Technical', 'Governance', 'Training'],
    datasets: [
      {
        label: 'Current Scores',
        data: [80, 70, 60],
        backgroundColor: 'rgba(79, 70, 229, 0.2)',
        borderColor: 'rgb(79, 70, 229)',
        pointBackgroundColor: 'rgb(79, 70, 229)',
      },
    ],
  };

  const historicalData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Overall Readiness',
        data: [65, 68, 70, 72, 74, 74],
        borderColor: 'rgb(79, 70, 229)',
        tension: 0.4,
        fill: false,
      },
    ],
  };

  const actionItems = [
    {
      title: 'Conduct Table-Top Exercise',
      priority: 'high',
      team: 'Crisis Response',
      dueDate: '2024-02-15',
    },
    {
      title: 'Update Response Procedures',
      priority: 'medium',
      team: 'Documentation',
      dueDate: '2024-02-28',
    },
    {
      title: 'Security Tool Training',
      priority: 'medium',
      team: 'Training',
      dueDate: '2024-03-10',
    },
  ];

  const benchmarks = [
    { metric: 'Overall Score', value: '74', benchmark: '64', difference: '+10' },
    { metric: 'Technical', value: '80', benchmark: '72', difference: '+8' },
    { metric: 'Governance', value: '70', benchmark: '65', difference: '+5' },
    { metric: 'Training', value: '60', benchmark: '55', difference: '+5' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Readiness Dashboard</h2>
        <Link
          to="/readiness/assessment"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          View Detailed Assessment
        </Link>
      </div>

      {/* Overall Score */}
      <div>
        <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">
          Overall Readiness Score
        </h3>
        <ReadinessScore score={readinessScore} />
      </div>

      {/* Last 30 Days */}
      <div>
        <h3 className="text-base font-semibold leading-6 text-gray-900">Last 30 Days</h3>
        <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
            <dt>
              <div className="absolute rounded-md bg-indigo-500 p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                </svg>
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">Incidents</p>
            </dt>
            <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">12</p>
              <p className="ml-2 flex items-baseline text-sm font-semibold text-red-600">
                <svg className="h-5 w-5 flex-shrink-0 self-center text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                </svg>
                <span className="sr-only">Increased by</span>
                25%
              </p>
            </dd>
          </div>

          <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
            <dt>
              <div className="absolute rounded-md bg-indigo-500 p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 4.5l-15 15m0 0h11.25m-11.25 0V8.25" />
                </svg>
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">Avg Response Time</p>
            </dt>
            <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">45m</p>
              <p className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                <svg className="h-5 w-5 flex-shrink-0 self-center text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 4.5l-15 15m0 0h11.25m-11.25 0V8.25" />
                </svg>
                <span className="sr-only">Decreased by</span>
                15%
              </p>
            </dd>
          </div>

          <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
            <dt>
              <div className="absolute rounded-md bg-indigo-500 p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                </svg>
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">Team Readiness</p>
            </dt>
            <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">92%</p>
              <p className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                <svg className="h-5 w-5 flex-shrink-0 self-center text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                </svg>
                <span className="sr-only">Increased by</span>
                5%
              </p>
            </dd>
          </div>

          <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
            <dt>
              <div className="absolute rounded-md bg-indigo-500 p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                </svg>
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">Compliance Score</p>
            </dt>
            <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">98%</p>
              <p className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                <svg className="h-5 w-5 flex-shrink-0 self-center text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                </svg>
                <span className="sr-only">Increased by</span>
                2%
              </p>
            </dd>
          </div>
        </dl>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4">Dimension Overview</h3>
          <div className="h-64">
            <RadarChart data={dimensionScores} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4">Historical Performance</h3>
          <div className="h-64">
            <LineChart data={historicalData} />
          </div>
        </div>
      </div>

      {/* Action Items */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4">Priority Actions</h3>
        <div className="space-y-4">
          {actionItems.map((item, index) => (
            <div key={index} className="flex items-center justify-between border-b pb-4 last:border-b-0">
              <div>
                <h4 className="font-medium">{item.title}</h4>
                <p className="text-sm text-gray-500">Team: {item.team}</p>
              </div>
              <div className="text-right">
                <span
                  className={clsx('px-2 py-1 text-xs rounded-full', {
                    'bg-red-100 text-red-800': item.priority === 'high',
                    'bg-yellow-100 text-yellow-800': item.priority === 'medium',
                    'bg-green-100 text-green-800': item.priority === 'low',
                  })}
                >
                  {item.priority}
                </span>
                <p className="text-sm text-gray-500 mt-1">Due: {item.dueDate}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Industry Benchmarks */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4">Industry Benchmarks</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {benchmarks.map((item, index) => (
            <div key={index} className="border rounded-lg p-4">
              <h4 className="text-sm text-gray-500">{item.metric}</h4>
              <div className="mt-2 flex items-baseline justify-between">
                <p className="text-2xl font-semibold text-gray-900">{item.value}</p>
                <div className="flex items-baseline">
                  <p className="text-sm text-gray-500">Avg: {item.benchmark}</p>
                  <span className="ml-2 text-sm font-semibold text-green-600">{item.difference}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}